/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { Row, Col, size } from '@everlywell/leaves';
import TrackVisibilityWrapper from 'components/TrackVisibilityWrapper';
import { GatsbyImage as Img, IGatsbyImageData } from 'gatsby-plugin-image';
import { throttle } from 'lodash-es';
import React, { useCallback, useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { animated as A, useSpring, config } from 'react-spring';
import ewAnalytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { sanitize } from 'utils/domHelpers';
import { CTA, ImageType, SectionBackgroundType } from 'utils/types';

import { OneImageDisplay } from './OneImageDisplay';
import * as S from './styles';

type ContentWithImage = {
  title: string;
  description: string;
  image: IGatsbyImageData;
  imageAlt: string;
};

export interface DigitalResultsSectionProps {
  uid?: string;
  title: string;
  headline: string;
  description?:
    | string
    | {
        childMarkdownRemark?: {
          html: string;
        };
      };
  mainCta?: CTA;
  listOfContent: ContentWithImage[];
  mobileImages: ImageType[];
  desktopImages: ImageType[];
  subHeadline?: string;
  subHeadlineDescription?: string;
  backgroundType?: SectionBackgroundType;
  disclaimer?: string;
  cta?: CTA;
  onCtaClick?: React.MouseEventHandler<HTMLButtonElement>;
}

// funtion for parallax effect in mobile
const calc = (o: number) => `translateY(${o * 0.075}px)`;

export const DigitalResultsSection: React.FC<DigitalResultsSectionProps> = ({
  uid = 'digitalResultsSection',
  title,
  headline,
  description,
  mainCta,
  mobileImages,
  desktopImages,
  listOfContent,
  subHeadline,
  subHeadlineDescription,
  backgroundType,
  disclaimer,
  cta,
  onCtaClick,
}) => {
  // state for triggering animation springs
  const [titleRef, titleInView] = useInView({ triggerOnce: true });

  // Animation Refs
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const [sectionInViewRef, sectionInView] = useInView();

  const setSectionRefs = useCallback(
    (node) => {
      sectionRef.current = node;
      sectionInViewRef(node);
    },
    [sectionInViewRef],
  );

  const [{ offset }, set] = useSpring(() => ({ offset: 0 }));

  // create parallax effect for desktop images onScroll
  const handleScroll: () => void = () => {
    if (!sectionRef || !sectionRef.current) {
      return;
    }
    const { bottom: sectionBottom } =
      sectionRef.current.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const offset = windowHeight - sectionBottom;
    set({ offset });
  };

  const handleScrollThrottled = throttle(handleScroll, 25);

  // attach the parallax scroll event only when this section is in view
  useEffect(() => {
    if (sectionInView) {
      window.addEventListener('scroll', handleScrollThrottled);
    } else {
      window.removeEventListener('scroll', handleScrollThrottled);
    }
    return () => {
      window.removeEventListener('scroll', handleScrollThrottled);
    };
  }, [sectionRef, sectionInView, handleScrollThrottled]);

  // triggered from scrolling into view
  const titleTransition = useSpring({
    opacity: titleInView ? 1 : 0,
    transform: titleInView ? 'translateY(0)' : `translateY(${size.xl1}px)`,
    config: config.slow,
  });

  let backgroundColor: 'Primary' | 'Secondary' | undefined;
  // default background color will be a gradient teal1 to white background
  if (backgroundType === SectionBackgroundType.None) {
    backgroundColor = 'Primary'; // background color will be all white
  } else if (backgroundType === SectionBackgroundType.FullWidth) {
    backgroundColor = 'Secondary'; // background color will be all teal1
  }

  const hasOnlyOneDesktopImage = desktopImages.length === 1;
  const hasOnlyOneMobileImage = mobileImages.length === 1;
  const hasOnlyOneImage = hasOnlyOneDesktopImage || hasOnlyOneMobileImage;

  const handleCtaClick: React.MouseEventHandler<HTMLButtonElement> = (
    event,
  ) => {
    if (onCtaClick) {
      onCtaClick(event);
    }

    ewAnalytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        sectionHeader: title ? title : headline,
        contentfulSection: 'DigitalResultsSection',
        ctaText: cta?.text,
      },
    });
  };

  return (
    <S.Wrapper
      ref={setSectionRefs}
      data-testid={`${uid}-section`}
      backgroundColor={backgroundColor}
    >
      <TrackVisibilityWrapper label={ANALYTICS.PDP.DIGITAL_RESULTS_SECTION}>
        <S.Container>
          {title ? (
            <Row center="xs">
              <Col xs={12} lg={10}>
                <A.div>
                  <S.SectionTitle text={title} />
                </A.div>
              </Col>
            </Row>
          ) : null}
          {headline ? (
            <Row center="xs">
              <Col xs={12} lg={10}>
                <A.div>
                  {headline ? (
                    <S.SectionHeadline>{headline}</S.SectionHeadline>
                  ) : null}
                </A.div>
              </Col>
            </Row>
          ) : null}

          {description && sectionInView ? (
            <Row center="xs">
              <Col xs={12} lg={10}>
                <A.div>
                  {typeof description === 'string' ? (
                    <S.HeadlineDescription>{description}</S.HeadlineDescription>
                  ) : (
                    <S.HeadlineDescriptionMarkdownDesktop
                      dangerouslySetInnerHTML={{
                        __html: sanitize(
                          description?.childMarkdownRemark?.html || '',
                        ),
                      }}
                    />
                  )}
                </A.div>
              </Col>
            </Row>
          ) : null}
          <S.RowContainer center="xs" hasOnlyOneImage={hasOnlyOneImage}>
            {/* has 3 images */}
            {!hasOnlyOneImage ? (
              <S.ParallaxImagesCol xs={12} md={7}>
                <S.MainImageContainer>
                  <S.MobileImageContainer>
                    {mobileImages[0] ? (
                      <S.MobileAnimatedImage aria-hidden="true">
                        <Img
                          image={mobileImages[0].gatsbyImageData}
                          alt={mobileImages[0].description || ''}
                          style={{ maxHeight: '412px' }}
                          imgStyle={{ objectFit: 'contain', left: '-60px' }}
                        />
                      </S.MobileAnimatedImage>
                    ) : null}
                    {mobileImages[1] ? (
                      <S.TopImage
                        aria-hidden="true"
                        style={{ transform: offset.to(calc) }}
                      >
                        <Img
                          image={mobileImages[1].gatsbyImageData}
                          alt={mobileImages[1].description || ''}
                        />
                      </S.TopImage>
                    ) : null}
                    {mobileImages[2] ? (
                      <S.BottomImage
                        aria-hidden="true"
                        style={{ transform: offset.to(calc) }}
                      >
                        <Img
                          image={mobileImages[2].gatsbyImageData}
                          alt={mobileImages[2].description || ''}
                        />
                      </S.BottomImage>
                    ) : null}
                  </S.MobileImageContainer>
                  <S.DesktopImageContainer>
                    {desktopImages[0] ? (
                      <S.MiddleImage aria-hidden="true">
                        <Img
                          image={desktopImages[0].gatsbyImageData}
                          alt={desktopImages[0].description || ''}
                        />
                      </S.MiddleImage>
                    ) : null}
                    {desktopImages[1] ? (
                      <S.TopImage
                        aria-hidden="true"
                        style={{ transform: offset.to(calc) }}
                      >
                        <Img
                          image={desktopImages[1].gatsbyImageData}
                          alt={desktopImages[1].description || ''}
                        />
                      </S.TopImage>
                    ) : null}
                    {desktopImages[2] ? (
                      <S.BottomImage style={{ transform: offset.to(calc) }}>
                        <Img
                          image={desktopImages[2].gatsbyImageData}
                          alt={desktopImages[2].description || ''}
                          aria-hidden="true"
                        />
                      </S.BottomImage>
                    ) : null}
                  </S.DesktopImageContainer>
                </S.MainImageContainer>
              </S.ParallaxImagesCol>
            ) : null}
            {/* has only 1 image */}
            {!!hasOnlyOneImage ? (
              <OneImageDisplay
                desktopImages={desktopImages}
                mobileImages={mobileImages}
                hasOnlyOneImage={hasOnlyOneImage}
              />
            ) : null}

            {description && typeof description !== 'string' && sectionInView && (
              <S.HeadlineDescriptionMarkdownMobile
                dangerouslySetInnerHTML={{
                  __html: sanitize(
                    description?.childMarkdownRemark?.html || '',
                  ),
                }}
              />
            )}
            <S.ColContainer xs={12} md={5}>
              <S.InfoBlockContainer ref={titleRef}>
                {listOfContent && listOfContent.length
                  ? listOfContent.map((content, index) => (
                      <S.AnimatedBlockContainer
                        key={index}
                        style={titleTransition}
                      >
                        <S.InfoBlock
                          title={content.title}
                          image={
                            <Img
                              image={content.image}
                              alt={content.imageAlt}
                              style={{ width: '100%', height: '100%' }}
                            />
                          }
                          imageAlt={content.imageAlt}
                          content={content.description}
                          size="small"
                        />
                      </S.AnimatedBlockContainer>
                    ))
                  : null}
                {subHeadline && subHeadlineDescription ? (
                  <S.Content>
                    <S.SubHeadline>{subHeadline}</S.SubHeadline>
                    <S.SubHeadlineDescription>
                      {subHeadlineDescription}
                    </S.SubHeadlineDescription>
                    {mainCta ? (
                      <S.CtaWrapper>
                        <S.CTA
                          data-testid={`${uid}-cta`}
                          href={mainCta.url}
                          appearance={
                            mainCta.appearance ? mainCta.appearance : 'primary'
                          }
                        >
                          {mainCta.text}
                        </S.CTA>
                      </S.CtaWrapper>
                    ) : null}
                  </S.Content>
                ) : null}

                <S.DesktopDisclaimer>{disclaimer}</S.DesktopDisclaimer>

                {cta ? (
                  <S.CtaWrapper>
                    <S.CTA
                      data-testid={`${uid}-cta`}
                      appearance="primary"
                      // @ts-ignore TODO: Temporary ts-ignore to not block a deploy.
                      onClick={handleCtaClick}
                      id={cta.url}
                      href={cta.url}
                    >
                      {cta.text}
                    </S.CTA>
                  </S.CtaWrapper>
                ) : null}

                <S.MobileDisclaimer>{disclaimer}</S.MobileDisclaimer>
              </S.InfoBlockContainer>
            </S.ColContainer>
          </S.RowContainer>
        </S.Container>
      </TrackVisibilityWrapper>
    </S.Wrapper>
  );
};

export default DigitalResultsSection;
